import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getUser } from "@/lib/api";
import Nav from "@/components/Nav";
import { Plus, ArrowRight, Zap, Share2, Crown, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [items, setItems] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, s] = await Promise.all([api.get("/me/assessments"), api.get("/subscriptions/me")]);
        setItems(a.data.items); setSub(s.data.subscription);
      } catch (e) { if (e?.response?.status === 401) nav("/auth"); }
      finally { setLoading(false); }
    })();
  }, [nav]);

  const start = async () => {
    const { data } = await api.post("/assessment/start");
    nav(`/assessment/${data.assessment_id}`);
  };

  const requestVerify = async () => {
    try { await api.post("/auth/email/verify/request", { origin_url: window.location.origin }); toast.success("Verification email sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };

  const subActive = sub && ["active", "trialing"].includes(sub.status);

  return (
    <div><Nav />
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">Your DualQ workspace</div>
        <h1 className="font-serif text-3xl sm:text-5xl mb-2 leading-tight" data-testid="dashboard-title">Welcome back, {user?.name || "explorer"}</h1>
        <p className="text-slate-400 max-w-xl text-sm sm:text-base">Track your DualQ measurements. Each assessment is versioned and reproducible.</p>

        {!user?.guest && user?.email && !user?.email_verified && (
          <div className="mt-6 rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3" data-testid="verify-banner">
            <Mail className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-amber-100">
              <strong>Verify your email</strong> to unlock report-ready notifications and secure password resets.
              <button onClick={requestVerify} className="ml-2 underline hover:text-white" data-testid="send-verify">Send verification email</button>
            </div>
          </div>
        )}

        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          <button onClick={start} className="group text-left rounded-2xl bg-[#131E36] border border-slate-800/70 hover:border-[#E0A96D]/40 p-5 transition-colors" data-testid="new-assessment-btn">
            <div className="flex items-center gap-2 text-[#E0A96D] text-xs font-mono uppercase tracking-widest">
              <Plus className="w-4 h-4" /> Start
            </div>
            <div className="font-serif text-xl mt-1">New DualQ assessment</div>
            <div className="text-xs text-slate-500 mt-1">Adaptive, mobile-first, ~15–20 minutes</div>
          </button>
          <button onClick={()=>nav(subActive ? "/plus" : "/billing")} className={`group text-left rounded-2xl p-5 transition-colors ${subActive ? "bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/60" : "bg-gradient-to-br from-[#131E36] to-[#1A2847] border border-[#E0A96D]/25 hover:border-[#E0A96D]/50"}`} data-testid="billing-card">
            <div className="flex items-center gap-2 text-[#E0A96D] text-xs font-mono uppercase tracking-widest">
              <Crown className="w-4 h-4" /> {subActive ? "DualQ Plus · Active" : "DualQ Plus"}
            </div>
            <div className="font-serif text-xl mt-1">{subActive ? "Open the premium hub" : "Unlock the full library · $4.99/mo"}</div>
            <div className="text-xs text-slate-500 mt-1">{subActive ? "Modules, patterns & progress" : "New assessments, deeper measurement"}</div>
          </button>
        </div>

        <h2 className="mt-10 mb-3 font-serif text-2xl">Your assessments</h2>
        <div className="space-y-3" data-testid="assessment-list">
          {loading && <div className="text-slate-500">Loading…</div>}
          {!loading && items.length === 0 && <div className="text-slate-500">No assessments yet. Start your first one.</div>}
          {items.map(a => (
            <div key={a.id} className="rounded-2xl bg-[#131E36] border border-slate-800/70 hover:border-[#E0A96D]/40 px-5 py-4 transition-colors" data-testid={`assessment-item-${a.id}`}>
              <div className="flex items-center justify-between gap-3">
                <button onClick={() => nav(a.status === "completed" ? `/results/${a.id}` : `/assessment/${a.id}`)} className="text-left flex-1 min-w-0">
                  <div className="font-serif text-xl truncate">{a.status === "completed" ? "Completed DualQ" : "In progress"}</div>
                  <div className="text-xs text-slate-500 font-mono mt-1">
                    v{a.assessment_version} · {new Date(a.started_at).toLocaleString()}
                    {a.unlocked && <span className="text-[#E0A96D] ml-2">· UNLOCKED</span>}
                  </div>
                </button>
                <ArrowRight className="w-5 h-5 text-slate-500 shrink-0" />
              </div>
              {a.status === "completed" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={()=>nav(`/challenge/${a.id}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#A855F7]/20 text-[#C084FC] text-xs hover:bg-[#A855F7]/30" data-testid={`dash-challenge-${a.id}`}>
                    <Zap className="w-3.5 h-3.5" /> Challenge
                  </button>
                  {a.unlocked && (
                    <>
                      <button onClick={()=>nav(`/report/${a.id}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#38BDF8]/20 text-[#7DD3FC] text-xs hover:bg-[#38BDF8]/30" data-testid={`dash-advanced-${a.id}`}>
                        Advanced report
                      </button>
                      <button onClick={()=>nav(`/share/${a.id}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E0A96D]/20 text-[#F0C28B] text-xs hover:bg-[#E0A96D]/30" data-testid={`dash-share-${a.id}`}>
                        <Share2 className="w-3.5 h-3.5" /> Share
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
